import {
    useInfinitePagination,
    useResources,
    useRouteMatch,
    useRouter,
} from '@funtech-int/rival-react';
import { Person, personQuery } from '@funtech-int/kp-graphql-sdk/yango-movies-www';
import { createInfiniteQuery } from '@funtech-int/rival';
import { useAuthManager } from '@funtech-int/yango-shared';

import { GraphqlClient } from '@/shared/lib/graphqlClient';
import { useInMemoryCache } from '@/shared/hooks/useInMemoryCache';
import { useGraphqlClient } from '@/shared/hooks/useGraphqlClient';
import { InMemoryCache } from '@/shared/lib/inMemoryCache';
import { getMovieUGCKey } from '@/shared/lib/movieUGC';
import { NotFoundAppError } from '@/shared/lib/errors/NotFoundAppError';

import { personRoute } from '../routes';

const FILMOGRAPHY_LIMIT = 25;

interface PersonParams {
    id: number;
    withUserData: boolean;
}

interface PersonQueryContext {
    client: GraphqlClient;
    inMemoryCache: InMemoryCache;
    params: PersonParams;
    url: string;
}

interface PersonPageInfo {
    offset: number;
}

const personResourceQuery = createInfiniteQuery<PersonQueryContext, Person, PersonPageInfo>({
    key(ctx) {
        return `${personQuery.name}:${ctx.params.id}`;
    },
    fetch(ctx, prev, pageInfo) {
        const offset = pageInfo?.offset ?? 0;

        return ctx.client
            .fetch({
                operation: personQuery,
                variables: {
                    id: ctx.params.id,
                    withUserData: ctx.params.withUserData,
                    filmographyOffset: offset,
                    filmographyLimit: FILMOGRAPHY_LIMIT,
                    withRating: false,
                },
            })
            .then(({ data }) => {
                if (!data || !data.filmography.items.length) {
                    throw new NotFoundAppError(ctx.url, {
                        message: 'Person not found',
                    });
                }

                data.filmography.items.forEach((item) => {
                    ctx.inMemoryCache.set(
                        getMovieUGCKey(item.id, 'plannedToWatch'),
                        Boolean(item.userData?.isPlannedToWatch)
                    );
                });

                const prevItems = prev?.filmography.items ?? [];

                // eslint-disable-next-line no-param-reassign
                data.filmography.items = prevItems.concat(data.filmography.items);

                return { ...data };
            });
    },
    nextPageInfo(data) {
        if (data && data.filmography.offset + FILMOGRAPHY_LIMIT < data?.filmography.total) {
            return {
                offset: data.filmography.offset + FILMOGRAPHY_LIMIT,
            };
        }
    },
    toJSON(data) {
        return data;
    },
    hydrate(data: any) {
        return personQuery.hydrate(data) as any;
    },
});

export function usePersonQuery() {
    const client = useGraphqlClient();
    const inMemoryCache = useInMemoryCache();
    const router = useRouter();

    const location = useRouteMatch(personRoute);
    const personId = location.params.id;

    const { isAuthorized } = useAuthManager();

    const pagination = useInfinitePagination();

    const [person] = useResources(
        personResourceQuery(
            {
                params: {
                    id: Number(personId),
                    withUserData: isAuthorized,
                },
                client,
                inMemoryCache,
                url: router.serialize(location),
            },
            { pagination }
        )
    );

    return { person, pagination };
}
